import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { escalateWorkOrder } from "../../../fetchers/cmmsWorkOrder";
import { fetchAssetHierarchyTree } from "../../../fetchers/cmmsAssetHierarchy";
import { getTrades } from "../../../fetchers/cmmsTrade";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { flattenAssetNodes } from "../../cmms/assetHierarchyUtils";
import { getPrefillTradeId, toTradeOptions } from "../../cmms/cmmsTradeAssignment";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getOutlinedButtonSx,
  getSemanticButtonSx,
} from "../../cmms/assetHierarchyStyles";

/*
 * Escalation moves the work order to another department or trade and keeps everything already on
 * it, so there is nothing here to rewrite it with. The old optional title and description fields
 * are gone: against a moving order they would overwrite the original problem statement, which is
 * the very history the receiving team is being handed.
 *
 * What is left is where it goes and a required reason. Where is a department, a trade or both
 * (AB#1624): a department alone lets its routing pick the trade, and a trade alone keeps the order
 * in its department — the machine has not moved, only the crew. The reason is posted as a work
 * order comment by the server, in the same transaction as the move, so it lands in the thread the
 * next technician is already reading.
 */
const emptyForm = {
  targetDepartmentId: null,
  targetTradeId: null,
  reason: "",
};

export default function EscalateDialog({
  open,
  onClose,
  workOrderId,
  companyKey,
  currentDepartmentId,
  currentTradeId,
  onSuccess,
}) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const treeQuery = useQuery(
    ["cmmsAssetHierarchyTree", companyKey, false],
    () => fetchAssetHierarchyTree(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 300000 },
  );

  // The work order form's key, so the two dialogs share one cache entry. Retired trades come
  // along with it, and toTradeOptions leaves them out: one cannot be given work.
  const tradesQuery = useQuery(
    ["cmmsTrades", companyKey, true],
    () => getTrades(companyKey, true),
    { enabled: open && Boolean(companyKey), staleTime: 30000 },
  );

  const flatNodes = useMemo(() => flattenAssetNodes(treeQuery.data), [treeQuery.data]);
  const trades = tradesQuery.data;

  // The department it is already in is left out. The server rejects escalating an order to where
  // it already sits, and offering the option only to reject it is a worse way to say so.
  const departmentOptions = useMemo(
    () => flatNodes
      .filter((node) => node.nodeType === 2 && node.id !== currentDepartmentId)
      .map((node) => ({ id: node.id, title: node.name })),
    [flatNodes, currentDepartmentId],
  );

  /*
   * The trade it already has stays in. Together with a new department it is a real hand-over —
   * the department moves and the crew follows — so it is only refused on its own, in validate.
   */
  const tradeOptions = useMemo(() => toTradeOptions(trades, form.targetTradeId), [trades, form.targetTradeId]);

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setErrors({});
    }
  }, [open]);

  const handleField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  /*
   * The work order form's pre-fill, for the same reason: what the server would derive for that
   * department alone, put on screen so the technician sees which crew gets the job. Nobody is
   * named after a hand-over, so there is no member to hold it back, and like the form it never
   * clears a trade that is already there.
   */
  const handleDepartmentChange = (departmentId) => {
    setForm((current) => {
      const prefill = getPrefillTradeId(departmentId, null, trades, flatNodes);
      return {
        ...current,
        targetDepartmentId: departmentId,
        ...(prefill != null ? { targetTradeId: prefill } : {}),
      };
    });
  };

  const validate = () => {
    const next = {};
    if (!form.targetDepartmentId && !form.targetTradeId) {
      next.targetDepartmentId = intl.formatMessage(messages.escalateTargetRequired);
    } else if (!form.targetDepartmentId && form.targetTradeId === currentTradeId) {
      next.targetTradeId = intl.formatMessage(messages.escalateSameTrade);
    }
    if (!form.reason?.trim()) next.reason = intl.formatMessage(messages.escalateReasonRequired);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await escalateWorkOrder(workOrderId, {
        companyKey,
        targetDepartmentId: form.targetDepartmentId ?? null,
        targetTradeId: form.targetTradeId ?? null,
        reason: form.reason.trim(),
      });
      pushNotification(intl.formatMessage(messages.toastWorkOrderEscalated));
      onSuccess?.(result);
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.escalateFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  const departmentValue = departmentOptions.find((option) => option.id === form.targetDepartmentId) ?? null;
  const tradeValue = tradeOptions.find((option) => option.id === form.targetTradeId) ?? null;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.escalateDialogTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          <FormattedMessage {...messages.escalateIntro} />
        </Typography>

        <AxionControls.AxionAutocomplete
          name="targetDepartmentId"
          label={intl.formatMessage(messages.escalateTargetDepartment)}
          options={departmentOptions}
          value={departmentValue}
          onChange={(_event, option) => handleDepartmentChange(option?.id ?? null)}
          error={errors.targetDepartmentId}
        />

        {/* Left out for a company with no trades, where it could only ever be empty. */}
        {tradeOptions.length > 0 && (
          <AxionControls.AxionAutocomplete
            name="targetTradeId"
            label={intl.formatMessage(messages.escalateTargetTrade)}
            options={tradeOptions}
            value={tradeValue}
            onChange={(_event, option) => handleField("targetTradeId", option?.id ?? null)}
            error={errors.targetTradeId}
          />
        )}

        {/*
          * A plain MUI TextField, like ReopenDialog's reason and unlike the pickers above it.
          * AxionMultiLineText accepts an `error` prop and renders it nowhere, so a required field
          * built on it fails silently — the submit does nothing and the box says why not.
          *
          * Five rows rather than three: this is the field the whole hand-over turns on, and a box
          * that looks like a one-liner gets a one-liner.
          */}
        <TextField
          label={intl.formatMessage(messages.escalateReason)}
          required
          value={form.reason}
          onChange={(event) => handleField("reason", event.target.value)}
          error={Boolean(errors.reason)}
          helperText={errors.reason}
          multiline
          rows={5}
          size="small"
          fullWidth
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}><FormattedMessage {...cmmsMessages.cancel} /></Button>
          <Button variant="contained" color="warning" onClick={handleSubmit} disabled={submitting} sx={getSemanticButtonSx()}>
            <FormattedMessage {...cmmsMessages.actionEscalate} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
